import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Logo from "@/components/Logo";
import {
  ChartLineUp,
  Vault,
  Calendar,
  Robot,
  Receipt,
  Users,
  ArrowRight,
  ShieldCheck,
  WhatsappLogo,
  MapPin,
} from "@phosphor-icons/react";

const WHATSAPP_NUMBER = "917709773338";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hi Profitink — I'd like to book a consultation."
)}`;

const services = [
  {
    icon: Calendar,
    title: "Deadline Radar",
    body: "Never miss a GST, TDS or ITR date. Color-coded calendar with form-level detail (GSTR-3B, ITR-3, Form 26Q).",
  },
  {
    icon: Robot,
    title: "AI Tax Co-pilot",
    body: "Chat with Claude Sonnet 4.5 or Gemini 3 Flash trained on Indian tax law nuances. Section 44AD? Composition scheme? Ask.",
  },
  {
    icon: Vault,
    title: "Encrypted Document Vault",
    body: "Share balance sheets, returns and certificates with clients under one signed roof.",
  },
  {
    icon: Users,
    title: "Client Ledger",
    body: "GSTIN, PAN, entity type — everything about your book of business in one dense grid.",
  },
  {
    icon: Receipt,
    title: "Retainer Invoicing",
    body: "Raise GST-compliant invoices in seconds. Track paid, unpaid and overdue across the roster.",
  },
  {
    icon: ChartLineUp,
    title: "Virtual CFO Dashboard",
    body: "Revenue, filings and workload at a glance. Built for CAs who advise, not just file.",
  },
];

const Landing = () => {
  return (
    <div className="marketing-scope min-h-screen bg-[#06120E] text-[#F9F6F0] font-sans overflow-x-hidden">
      {/* NAV */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#06120E]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <Logo size={34} tone="gold" wordmarkClassName="text-xl text-[#F9F6F0]" />
          </Link>
          <div className="hidden md:flex items-center gap-10 text-sm text-white/70">
            <a href="#services" className="hover:text-[#D4AF37] transition-colors" data-testid="nav-services">Services</a>
            <a href="#approach" className="hover:text-[#D4AF37] transition-colors" data-testid="nav-approach">Approach</a>
            <a href="#pricing" className="hover:text-[#D4AF37] transition-colors" data-testid="nav-pricing">Pricing</a>
            <Link to="/audit" className="hover:text-[#D4AF37] transition-colors" data-testid="nav-audit">Diagnostic</Link>
            <a href="#contact" className="hover:text-[#D4AF37] transition-colors" data-testid="nav-contact">Contact</a>
          </div>
          <Link
            to="/auth"
            className="px-5 py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F1C15] transition-colors text-sm tracking-wide"
            data-testid="nav-login-btn"
          >
            Client Login
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 opacity-30 noise-overlay pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6"
            >
              Chartered Accountants · Virtual CFO · Tax Consulting
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-serif-display text-5xl md:text-7xl leading-[1.02] tracking-tight"
            >
              Finance mastery for
              <br />
              <span className="italic text-[#D4AF37]">founders who mean business.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed"
            >
              A modern practice built around GST, TDS and Income Tax — augmented by an
              AI tax co-pilot that reads your books, not the internet. India-first, deadline-aware.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/audit"
                className="group brand-gold-bg px-8 py-4 flex items-center gap-3 text-sm tracking-wider uppercase font-semibold hover:opacity-90"
                data-testid="hero-cta-primary"
              >
                Book the ₹8,000 diagnostic <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 border border-white/20 text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] text-sm tracking-wider uppercase transition-colors flex items-center gap-2"
                data-testid="hero-cta-whatsapp"
              >
                <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" /> Chat on WhatsApp
              </a>
            </motion.div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
              <Stat value="₹47Cr" label="Advised revenue" />
              <Stat value="35+" label="Businesses served" />
              <Stat value="15 yrs" label="At the ledger" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative aspect-[4/5] overflow-hidden"
            >
              <img
                src="https://images.pexels.com/photos/17753331/pexels-photo-17753331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06120E] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 border-t border-[#D4AF37]/60 pt-4">
                <p className="text-[#D4AF37] font-serif-display text-2xl italic">&ldquo;Numbers should serve strategy.&rdquo;</p>
                <p className="text-white/60 text-xs tracking-widest uppercase mt-2">— CA Shilpa Vishwen</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid grid-cols-12 gap-8 mb-16">
            <div className="col-span-12 md:col-span-4">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">What we do</p>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
                A full-stack tax office, <span className="italic">re-imagined.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6 text-white/70 leading-relaxed self-end">
              We built the tools we always wished existed — a deadline radar, a document vault,
              a client ledger and an AI tax assistant fluent in Indian statutes — and packed
              them behind a single client portal so nothing falls through the cracks.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
            {services.map((s) => (
              <div
                key={s.title}
                className="p-8 border-r border-b border-white/10 hover:bg-[#0A1B14] transition-colors group"
                data-testid={`service-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <s.icon size={32} weight="duotone" className="text-[#D4AF37]" />
                <h3 className="font-serif-display text-2xl mt-6">{s.title}</h3>
                <p className="text-white/60 mt-3 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section id="approach" className="border-t border-white/5 bg-[#0A1B14]/30">
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">The approach</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-6">
              Advisors first. <span className="italic">Compliers second.</span>
            </h2>
            <p className="text-white/70 leading-relaxed">
              Anyone can file a GSTR-3B. Fewer can tell you whether Section 44ADA
              is quietly costing you ₹4L a year. Every engagement begins with a
              two-hour audit of your books — free of cost, no obligations.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 space-y-6">
            {[
              { n: "01", t: "Diagnostic", d: "We read the last four quarters. Identify leaks." },
              { n: "02", t: "Structuring", d: "Right entity, right regime, right accounting method." },
              { n: "03", t: "Ongoing CFO", d: "Monthly filings, quarterly strategy, 24×7 AI backup." },
            ].map((step) => (
              <div key={step.n} className="grid grid-cols-12 gap-6 items-center py-6 border-b border-white/10">
                <div className="col-span-2 font-mono-data text-[#D4AF37] text-2xl">{step.n}</div>
                <div className="col-span-10">
                  <h4 className="font-serif-display text-2xl">{step.t}</h4>
                  <p className="text-white/60 mt-1">{step.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Retainers</p>
            <h2 className="font-serif-display text-4xl md:text-5xl">Choose your engagement.</h2>
            <p className="mt-5 text-white/60 max-w-2xl mx-auto text-sm md:text-base">
              Every retainer is <span className="text-[#D4AF37] italic">fully customizable</span> — mix modules, adjust entry limits or add scope. Talk to us and we&rsquo;ll shape a plan around your books.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Compliance", price: "₹10,900", tag: "/ month", features: ["Accounting up to 50 entries", "GST monthly", "TDS quarterly", "ITR filing", "Basic advisory"] },
              { name: "Growth", price: "₹29,900", tag: "/ month", features: ["Everything in Compliance", "Accounting up to 150 entries", "Monthly CFO call", "AI tax assistant"], featured: true },
              { name: "Virtual CFO", price: "₹89,000", tag: "/ month", features: ["Everything in Growth", "Accounting up to 150 entries", "3 CFO calls monthly", "Monthly MIS reports", "Board reporting", "Priority WhatsApp"] },
            ].map((p) => (
              <div
                key={p.name}
                className={`p-8 border ${p.featured ? "border-[#D4AF37] bg-[#0A1B14]" : "border-white/10"} flex flex-col`}
                data-testid={`pricing-${p.name.toLowerCase()}`}
              >
                {p.featured && (
                  <span className="self-start text-[10px] tracking-widest uppercase brand-gold-bg px-2 py-1 mb-4">Most chosen</span>
                )}
                <h3 className="font-serif-display text-3xl">{p.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-mono-data text-4xl text-[#D4AF37]">{p.price}</span>
                  <span className="text-white/50 text-sm">{p.tag}</span>
                </div>
                <ul className="mt-8 space-y-3 text-sm text-white/70 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <ShieldCheck size={16} weight="duotone" className="text-[#D4AF37]" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/auth"
                  className={`mt-8 py-3 text-center text-sm tracking-widest uppercase ${p.featured ? "brand-gold-bg" : "border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37]"} transition-colors`}
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="border-t border-white/5 bg-[#0A1B14]/40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 noise-overlay pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-12 gap-10 relative">
          <div className="col-span-12 lg:col-span-6">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Get in touch</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
              A ₹8,000 diagnostic,
              <br />
              <span className="italic text-[#D4AF37]">fully refundable.</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-lg leading-relaxed">
              Share your last four quarters. We&rsquo;ll return with a written PDF diagnostic &mdash;
              leaks, tax positions worth revisiting, and a clean roadmap. Adjusted against your
              first month if you sign a retainer; refunded in full if you don&rsquo;t see the value.
            </p>
            <Link
              to="/audit"
              data-testid="contact-learn-audit"
              className="group inline-flex items-center gap-2 mt-8 text-[#D4AF37] text-sm tracking-widest uppercase hover:gap-3 transition-all"
            >
              How the diagnostic works <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="col-span-12 lg:col-span-5 lg:col-start-8 space-y-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="group flex items-center justify-between border border-white/15 hover:border-[#D4AF37] p-6 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 border border-white/20 group-hover:border-[#25D366] flex items-center justify-center transition-colors">
                  <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">WhatsApp</div>
                  <div className="font-mono-data text-lg text-[#F9F6F0]">+91 77097 73338</div>
                </div>
              </div>
              <ArrowRight size={18} weight="bold" className="text-white/40 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
            </a>

            <div className="flex items-center gap-4 border border-white/15 p-6">
              <div className="w-12 h-12 border border-white/20 flex items-center justify-center">
                <MapPin size={22} weight="duotone" className="text-[#D4AF37]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/50">Based in</div>
                <div className="font-serif-display text-xl text-[#F9F6F0]">Bengaluru, India</div>
              </div>
            </div>

            <Link
              to="/auth"
              data-testid="contact-cta-primary"
              className="group brand-gold-bg w-full py-5 flex items-center justify-center gap-3 text-sm tracking-widest uppercase font-semibold hover:opacity-90"
            >
              Create your client account <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/50">
          <p>© {new Date().getFullYear()} Profitink — Chartered Accountants</p>
          <p className="font-mono-data text-xs">MADE IN INDIA · FILED ON TIME</p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-btn"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white px-4 py-3 shadow-lg shadow-black/40 transition-colors group"
      >
        <WhatsappLogo size={22} weight="fill" />
        <span className="hidden sm:inline text-sm font-semibold tracking-wide">WhatsApp us</span>
      </a>
    </div>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <div className="font-mono-data text-3xl text-[#D4AF37]">{value}</div>
    <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{label}</div>
  </div>
);

export default Landing;
